import { OrderStatus as OrderStatusEnum } from '@/components/order/order.types';
import { ListOrdersView, useListOrders } from '@/hooks/orders/useListOrders';
import type { HttpStatus } from '@montugroup/apis-styleguide';
import type { apiPharmacyContract } from '@montugroup/circuit-api-contracts';
import { getTsPharmacyClient } from '@montugroup/circuit-shared/api/pharmacy.client';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared/constants/shippingOptions';
import type { OrderStatus } from '@montugroup/circuit-shared/types/index';
import { noOp } from '@montugroup/circuit-shared/utils/noOp';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { useQueryClient } from '@tanstack/react-query';
import type { ClientInferResponseBody } from '@ts-rest/core';
import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export type ListOrdersResponse = ClientInferResponseBody<
  typeof apiPharmacyContract.listOrders,
  typeof HttpStatus.OK
>['data'];

export type Order = ArrayElement<ListOrdersResponse[keyof ListOrdersResponse]>;

type TableData = Record<
  Exclude<(typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'], 'ThreeHour'>,
  Order[] | null | undefined
>;

type DeliveryTableDataProviderProps = {
  view: ListOrdersView;
};

type DeliveryTableDataContext = {
  view: ListOrdersView;
  data?: TableData;
  isLoading: boolean;
  refetch: () => Promise<any>;
  filter: string;
  setFilter: (filter: string) => void;
  statusFilters: OrderStatus[];
  setStatusFilters: (statusFilters: OrderStatus[]) => void;
};

export const DeliveryTableDataContext = createContext<DeliveryTableDataContext>({
  view: ListOrdersView.Open,
  isLoading: true,
  refetch: () => Promise.resolve(undefined),
  filter: '',
  setFilter: noOp,
  statusFilters: [],
  setStatusFilters: noOp,
});

function isOrderMatch(order: Order, filter: string): boolean {
  const filterLower = filter.toLowerCase().trim();

  const fieldsToSearch = [order.orderCode, order.patient?.patientCode, order.patient?.name];

  return fieldsToSearch.some((value) => typeof value === 'string' && value.toLowerCase().includes(filterLower));
}

const client = getTsPharmacyClient();

export function DeliveryTableDataProvider({ view, children }: PropsWithChildren<DeliveryTableDataProviderProps>) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('');
  const [statusFilters, setStatusFilters] = useState<OrderStatus[]>([]);

  const { data, isLoading } = useListOrders({
    view,
    statusFilters: statusFilters.map((orderStatus) => orderStatus.name),
    filter,
  });

  const refetch = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: client.listOrders.keys });
  }, [queryClient]);

  const filteredData = useMemo(() => {
    if (!data?.body?.data) {
      return undefined;
    }

    const statusFilterNames = statusFilters.map((statusFilterOrderStatus) => statusFilterOrderStatus.name);

    const statusMatches = (orderStatus: string) =>
      statusFilters.length === 0 || statusFilterNames.includes(orderStatus);

    const getStatusPriority = (status: string) => {
      switch (status) {
        case OrderStatusEnum.PharmacyOutOfStock:
          return 3;
        case OrderStatusEnum.IntervalTime:
          return 2;
        default:
          return 1;
      }
    };

    const sortOrders = (orders: Order[]) => {
      return orders
        .filter((order) => {
          const statusMatch = statusMatches(order.status);
          const searchMatch = !filter || isOrderMatch(order, filter);
          return statusMatch && searchMatch;
        })
        .sort((a, b) => getStatusPriority(a.status) - getStatusPriority(b.status));
    };

    return {
      [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDay].name]: data?.body?.data?.SameDay && sortOrders(data.body.data.SameDay),
      [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].name]:
        data?.body?.data?.SameDayAfternoon && sortOrders(data.body.data.SameDayAfternoon),
      [DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name]:
        data?.body?.data?.ClickAndCollect && sortOrders(data.body.data.ClickAndCollect),
      [DELIVERY_OPTIONS[DELIVERY_OPTION.Standard].name]:
        data?.body?.data?.Standard && sortOrders(data.body.data.Standard),
    };
  }, [data, filter, statusFilters]);

  const value = useMemo(
    () => ({
      view,
      data: filteredData,
      isLoading,
      refetch,
      filter,
      setFilter,
      statusFilters,
      setStatusFilters,
    }),
    [view, filteredData, isLoading, refetch, filter, statusFilters],
  );

  return <DeliveryTableDataContext.Provider value={value}>{children}</DeliveryTableDataContext.Provider>;
}

export const useDeliveryTableData = () => {
  const { data, isLoading, refetch, view } = useContext(DeliveryTableDataContext);
  return { data, isLoading, refetch, view };
};

export const useDeliveryTableFilters = () => {
  const { filter, setFilter, statusFilters, setStatusFilters } = useContext(DeliveryTableDataContext);
  return { filter, setFilter, statusFilters, setStatusFilters };
};
